import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';

import type { AISystemOnboardingContext, AISystemRegistrationStatus } from '@/clients/v2';
import type { ScoutScope } from '@/features/assistant/utils/scoutScope.ts';
import { tabStorage } from '@/utils/tabStorage.ts';

const OPEN_STATE_KEY = 'scout:panel:open';

export interface ScoutPageContext {
  projectId: string;
  projectName?: string | null;
  registrationStatus?: AISystemRegistrationStatus | null;
  onboardingContext?: () => AISystemOnboardingContext;
  contextLoading?: () => boolean;
  /** Folds the history so the panel leaves the page's form usable. */
  foldHistory?: boolean;
}

export const useScoutPanelStore = defineStore('scout-panel', () => {
  // Open on first visit; e2e seeds it closed in tests/utils/auth.ts. Each tab keeps its own state.
  const isOpen = useStorage(OPEN_STATE_KEY, true, tabStorage);
  const pageContext = shallowRef<ScoutPageContext | null>(null);
  const historyOpen = ref(true);
  const newChatScope = shallowRef<ScoutScope | null>(null);
  const headerAnchorEl = shallowRef<HTMLElement | null>(null);
  const dialogAnchorEls = shallowRef<HTMLElement[]>([]);
  const anchorEl = computed(() => dialogAnchorEls.value.at(-1) ?? headerAnchorEl.value);
  const besideDialog = computed(() => dialogAnchorEls.value.length > 0);
  const answerReady = ref(false);

  function open() {
    isOpen.value = true;
    answerReady.value = false;
  }

  function close() {
    isOpen.value = false;
  }

  function toggle() {
    if (isOpen.value) close();
    else open();
  }

  function markAnswerReady() {
    answerReady.value = true;
  }

  function providePageContext(context: ScoutPageContext) {
    if (context.foldHistory && pageContext.value?.projectId !== context.projectId) {
      historyOpen.value = false;
    }
    pageContext.value = context;
  }

  function setHistoryOpen(open: boolean) {
    historyOpen.value = open;
  }

  function setNewChatScope(scope: ScoutScope | null) {
    newChatScope.value = scope;
  }

  // Route swaps can mount the next page before the previous one unmounts: only the page that set
  // the context clears it.
  function clearPageContext(context: ScoutPageContext) {
    if (pageContext.value === context) pageContext.value = null;
  }

  // Dialog anchors stay in document order: the topmost dialog's portal comes last.
  function registerAnchor(el: HTMLElement, inDialog: boolean) {
    if (!inDialog) {
      headerAnchorEl.value = el;
      return;
    }
    dialogAnchorEls.value = [...dialogAnchorEls.value, el].sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    );
  }

  function releaseAnchor(el: HTMLElement) {
    if (headerAnchorEl.value === el) headerAnchorEl.value = null;
    dialogAnchorEls.value = dialogAnchorEls.value.filter((anchor) => anchor !== el);
  }

  function focusAnchor() {
    anchorEl.value?.focus();
  }

  function isAnchor(el: Element | null) {
    return el !== null && el === anchorEl.value;
  }

  function anchorCenter(): { x: number; y: number } | null {
    const rect = anchorEl.value?.getBoundingClientRect();
    if (!rect) return null;
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  return {
    isOpen,
    pageContext,
    historyOpen,
    besideDialog,
    newChatScope,
    answerReady,
    open,
    close,
    toggle,
    markAnswerReady,
    providePageContext,
    clearPageContext,
    setHistoryOpen,
    setNewChatScope,
    registerAnchor,
    releaseAnchor,
    focusAnchor,
    isAnchor,
    anchorCenter,
  };
});
